import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { enquiryService } from '../api/axios';
import AttachmentViewer from '../components/AttachmentViewer';

import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  List, ListItem, ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Grid
} from '@mui/material';

import '../styles/_utilities.css';

function ConversationModal({ enquiry, enquiryItem, onClose, onOpenFilePreview, isEnquiryLevel = false }) {
  const { user } = useContext(AuthContext);
  const [messageText, setMessageText] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [isLoadingInteractions, setIsLoadingInteractions] = useState(true);

  // --- HELPER: Safely parse dates from Array or String format ---
  const safeParseDate = (timestamp) => {
    if (!timestamp) return null;

    // Handle Java LocalDate/LocalDateTime Array format: [2025, 1, 22] or [2025, 1, 22, 14, 30]
    if (Array.isArray(timestamp)) {
      const [year, month, day, hour, minute, second] = timestamp;
      // JS Months are 0-indexed (0=Jan, 1=Feb), Java is 1-indexed
      return new Date(Date.UTC(year, month - 1, day, hour || 0, minute || 0, second || 0));
    }

    // Handle String format
    if (typeof timestamp === 'string') {
      // If it looks like ISO but missing Z/Offset, append Z to treat as UTC
      const timeString = !(timestamp.endsWith('Z') || timestamp.includes('+'))
        ? timestamp + 'Z'
        : timestamp;
      return new Date(timeString);
    }

    // Fallback for number/timestamp
    return new Date(timestamp);
  };

  const fetchInteractions = useCallback(async () => {
    if (!enquiry?.id) {
        setIsLoadingInteractions(false);
        return;
    }

    setIsLoadingInteractions(true);
    setError('');
    try {
      const response = await enquiryService.get(`/enquiry/${enquiry.id}/interactions`);
      const allInteractions = Array.isArray(response.data) ? response.data : [];

      // Sort using the safe parser to avoid NaN errors
      allInteractions.sort((a, b) => {
        const dateA = safeParseDate(a.timestamp) || new Date(0);
        const dateB = safeParseDate(b.timestamp) || new Date(0);
        return dateA - dateB;
      });

      setInteractions(allInteractions);
    } catch (err) {
      setError("Could not load conversation.");
      console.error("Error fetching interactions:", err);
    } finally {
      setIsLoadingInteractions(false);
    }
  }, [enquiry]);

  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    if (!messageText.trim() && files.length === 0) return;

    setIsSubmitting(true);
    setError('');
    const formData = new FormData();
    formData.append("messageText", messageText);
    files.forEach(file => formData.append(`files`, file));

    try {
      const url = isEnquiryLevel
        ? `/enquiry/${enquiry.id}/interact`
        : `/enquiry/item/${enquiryItem.id}/interact`;

      await enquiryService.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setMessageText('');
      setFiles([]);
      fetchInteractions();
    } catch (err) {
      setError(`Failed to send message: ${err.response?.data?.message || 'Error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusChipColor = (status) => {
    const s = status ? status.toLowerCase() : '';
    if (s.includes('closed won')) return 'success';
    if (s.includes('closed lost')) return 'error';
    if (s.includes('pending')) return 'warning';
    if (s.includes('on hold')) return 'info';
    return 'default';
  };

  const modalTitle = isEnquiryLevel
    ? `Overall Conversation for: ${enquiry?.uniqueId}`
    : `Item Conversation: ${enquiryItem?.name}`;

  const filteredInteractions = isEnquiryLevel
    ? interactions.filter(interaction => interaction.enquiryItemId === null)
    : interactions.filter(interaction => enquiryItem && interaction.enquiryItemId === enquiryItem.id);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h2" component="h2">{modalTitle}</Typography>
          <Button onClick={onClose} color="inherit" sx={{minWidth: 'auto', p: 1}}>&times;</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {enquiryItem && !isEnquiryLevel && (
          <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h3">Item Details</Typography>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <Typography><strong>Description:</strong> {enquiryItem.description || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography><strong>Quantity:</strong> {enquiryItem.quantity || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography>
                        <strong>Budget Per Unit:</strong> {
                            enquiryItem.budgetPerUnit ?
                            enquiryItem.budgetPerUnit.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }) :
                            'N/A'
                        }
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography><strong>Delivery Location:</strong> {enquiryItem.deliveryLocation || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography>
                        <strong>Target Delivery Date:</strong> {
                            (() => {
                                const dateObj = safeParseDate(enquiryItem.targetDateOfDelivery);
                                return dateObj ?
                                    dateObj.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' (IST)' :
                                    'N/A';
                            })()
                        }
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography>
                        <strong>Status:</strong> <Chip label={enquiryItem.status} color={getStatusChipColor(enquiryItem.status)} size="small" />
                    </Typography>
                </Grid>
            </Grid>
            <AttachmentViewer attachments={enquiryItem.imageAttachments} onOpenFilePreview={onOpenFilePreview} />
          </Box>
        )}
        <Typography variant="h4">History</Typography>
        {isLoadingInteractions ? <CircularProgress sx={{display: 'block', mx: 'auto', my: 2}} /> : error ? <Alert severity="error">{error}</Alert> : (
            <List sx={{ maxHeight: '40vh', overflowY: 'auto', p: 1 }}>
              {filteredInteractions.length === 0 ? (
                <ListItem><ListItemText primary="No interactions for this specific view." /></ListItem>
              ) : (
                filteredInteractions.map(interaction => (
                  <ListItem key={interaction.id} sx={{
                    mb: 1.5,
                    bgcolor: interaction.isSystemMessage ? 'grey.200' : 'background.default',
                    p: 1.5,
                    borderRadius: 2
                  }}>
                    <ListItemText
                      primary={
                        <Typography color="text.primary">
                          {interaction.isSystemMessage ? (
                            <em>{interaction.messageText}</em>
                          ) : (
                            <>
                              <strong>{interaction.commentedBy} ({interaction.commentedByRole}):</strong> {interaction.messageText}
                            </>
                          )}
                        </Typography>
                      }
                      secondary={
                          <>
                            {!interaction.isSystemMessage && (
                              <AttachmentViewer attachments={interaction.imageAttachments} onOpenFilePreview={onOpenFilePreview} />
                            )}
                            <Typography variant="caption" display="block" mt={1} color="text.secondary">
                              {(() => {
                                const dateObj = safeParseDate(interaction.timestamp);
                                return dateObj && !isNaN(dateObj)
                                  ? dateObj.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' (IST)'
                                  : 'Date N/A';
                              })()}
                            </Typography>
                          </>
                        }
                    />
                  </ListItem>
                ))
              )}
            </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <form onSubmit={handleAddInteraction} style={{ width: '100%' }}>
            <TextField fullWidth multiline rows={3} value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="Type your message..." />
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
                <Button variant="outlined" component="label">Attach Files<input type="file" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files))} /></Button>
                {files.length > 0 && <Typography variant="body2">{files.length} file(s) selected</Typography>}
                <Button type="submit" variant="contained" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Send Message'}</Button>
            </Stack>
        </form>
      </DialogActions>
    </Dialog>
  );
}

export default ConversationModal;